// PPC Farm Mail Tracker Types
// Phase 2: Backend expansion with new ledger types

import { Timestamp } from "firebase/firestore";
import { Timestamp as AdminTimestamp } from "firebase-admin/firestore";

// Target Ledger data structure (renamed from PpcFarmLedgerData)
export interface PpcFarmTargetLedgerData {
  id: string;
  rowIndex: number;
  cohort: string; // Required (Column B)
  cohortNumberNumeric: number; // NEW: Numerical cohort for proper sorting
  sellerName: string; // Required (Column C)
  sellerName_lowercase: string; // SEARCH OPTIMIZATION: Lowercase version for case-insensitive search
  sellerAddress: string; // Required (Column D)
  sellerAddress_lowercase: string; // NEW: Lowercase version for case-insensitive search
  fullAddress: string; // Optional (Column E) - fallback to sellerAddress
  dateAdded: Timestamp; // Required (Column F)
  addedBy: string; // Required (Column G)
  sellerPageLink: string | null; // Optional (Column I)
  totalRatings: number | null; // Optional (Column J)
  starRating: number | null; // Optional (Column K)
  sellerDescription: string | null; // Optional (Column L)
  sellerPhone: string | null; // Optional (Column M)
  productCount: number | null; // Optional (Column N)
  otherBrands: string | null; // Optional (Column O)
  searchKeywords: string[]; // NEW: Array of keywords for efficient searching
  createdAt: Timestamp; // NEW: Server timestamp for tie-breaker sorting
  syncId: string;
}

// Cohort Ledger data structure
export interface PpcFarmCohortLedgerData {
  id: string;
  rowIndex: number;
  cohortNumber: string; // Column A - Required
  cohortNumberNumeric: number; // NEW: Numerical cohort for proper sorting
  dateCreated: Timestamp; // Column B - Required
  yourName: string; // Column C - Required
  count: number; // Column D - Required
  filters_raw: string; // Column E - Required (original filter string)
  filters_structured: Record<string, any>; // Column E - Parsed structured filters
  mailerShortDescription: string | null; // Column F - Optional
  isTriedAndTrue: boolean; // Derived from mailerShortDescription
  discoveryCall: number | null; // Column G - Optional
  signedClients: number | null; // Column H - Optional
  conversionRateSignedClients: number | null; // Column I - Optional
  createdAt: Timestamp; // NEW: Server timestamp for tie-breaker sorting
  // Columns J and K excluded as requested
  syncId: string; // SHA256 hash for deduplication
}

// Mailing Ledger data structure
export interface PpcFarmMailingLedgerData {
  id: string;
  rowIndex: number;
  dateEntered: Timestamp; // Column A - Required (validation key)
  dateMailed: Timestamp | null; // Column B - Optional
  name: string; // Column C - Required (validation key)
  cohort: string | null; // Column D - Optional
  cohortNumberNumeric: number | null; // NEW: Numerical cohort for proper sorting
  cohortQuantity: number | null; // Column E - Optional
  addressingType: string; // Column F - Required (validation key)
  mailerId: string | null; // Column G - Optional
  customUrl: string | null; // Column H - Optional
  mailerHeadline: string | null; // Column I - Optional
  linkToMailer: string | null; // Column J - Optional
  notes: string | null; // Column K - Optional
  includeInMailingCostDashboard: boolean | null; // Column L - Optional
  dropNumber: number | null; // Column M - Optional
  addedBy: string | null; // NEW: Added by field for filtering
  searchKeywords: string[]; // NEW: Array of keywords for efficient searching
  createdAt: Timestamp; // NEW: Server timestamp for tie-breaker sorting
  syncId: string; // SHA256 hash for deduplication
}

// NEW: Enhanced Mailing Ledger data structure with financial data from Mailing Cost Dashboard
export interface PpcFarmMailingLedgerDataEnhanced extends PpcFarmMailingLedgerData {
  // Financial fields joined from Mailing Cost Dashboard tab
  mailingCost: number | null; // Column E from Mailing Cost Dashboard
  extraAssociatedCosts: number | null; // Column F from Mailing Cost Dashboard
  costNotes: string | null; // Column H from Mailing Cost Dashboard (cost-specific notes)
}

// NEW: Direct Mail Cost data structure
export interface PpcFarmDirectMailCostData {
  id: string;
  rowIndex: number;
  item: string; // Column A - Item (e.g., "Direct Mail Campaign", "Data Scrape")
  category: string; // Column B - Category (e.g., "Direct Mail")
  expenseDate: Timestamp; // Column C - Date (converted to Firestore Timestamp)
  amount: number; // Column D - Amount (converted from currency string to number)
  isCampaignCost: boolean; // NEW: Logic-derived field - true if item is "Direct Mail Campaign"
  createdAt: Timestamp; // Server timestamp for audit trail
  syncId: string; // SHA256 hash for deduplication
}

// NEW: Report History Ledger data structure
export interface PpcFarmReportHistoryLedgerData {
  id: string;
  rowIndex: number;
  eventDate: Timestamp; // Column A - Date (converted to Firestore Timestamp)
  cohort: string; // Column B - Cohort # (e.g., "Cohort 14")
  drop: string; // Column C - Drop (e.g., "first")
  eventName: string; // Column D - Event Name (e.g., "page_view", "session_start")
  totalUsers: number; // Column E - Total users (converted to number)
  eventCount: number; // Column F - Event count (converted to number)
  createdAt: Timestamp; // Server timestamp for audit trail
  syncId: string; // SHA256 hash for deduplication
}

// Dashboard statistics interface
export interface PpcFarmStatistics {
  totalRecords: number;
  uniqueCohorts: number;
  uniqueSellers: number;
  lastUpdated?: Timestamp;
}

// Filter and search options - Generic for all ledger types
export interface PpcFarmFilters {
  search: string;
  cohort: string;
  addedBy: string;
}

// Cohort Ledger specific filters
export interface PpcFarmCohortFilters {
  search: string;
  yourName: string;
  isTriedAndTrue: string; // 'all' | 'tried-and-true' | 'experimental'
}

// Cohort Dashboard specific sort options
export interface PpcFarmCohortDashboardSortOptions {
  field:
    | "cohortNumber"
    | "count"
    | "conversionRateSignedClients"
    | "dateCreated";
  direction: "asc" | "desc";
}

// Mailing Ledger specific filters
export interface PpcFarmMailingFilters {
  search: string;
  cohort: string;
  addressingType: string;
  name: string;
}

// NEW: Direct Mail Cost specific filters
export interface PpcFarmDirectMailCostFilters {
  search: string;
  category: string;
  item: string;
  isCampaignCost: string; // 'all' | 'campaign' | 'operational'
  dateRange: {
    start: string | null;
    end: string | null;
  };
}

// NEW: Report History Ledger specific filters
export interface PpcFarmReportHistoryFilters {
  search: string;
  cohort: string;
  drop: string;
  eventName: string;
  dateRange: {
    start: string | null;
    end: string | null;
  };
}

// Generic filter type
export type PpcFarmGenericFilters =
  | PpcFarmFilters
  | PpcFarmCohortFilters
  | PpcFarmMailingFilters
  | PpcFarmDirectMailCostFilters
  | PpcFarmReportHistoryFilters;

// Table sorting options - Generic for all ledger types
export interface PpcFarmSortOptions {
  field: string;
  direction: "asc" | "desc";
}

// Target Ledger specific sort options (for backward compatibility)
export interface PpcFarmTargetSortOptions {
  field: keyof PpcFarmTargetLedgerData;
  direction: "asc" | "desc";
}

// Cohort Ledger specific sort options
export interface PpcFarmCohortSortOptions {
  field: keyof PpcFarmCohortLedgerData;
  direction: "asc" | "desc";
}

// Mailing Ledger specific sort options
export interface PpcFarmMailingSortOptions {
  field: keyof PpcFarmMailingLedgerData;
  direction: "asc" | "desc";
}

// NEW: Direct Mail Cost specific sort options
export interface PpcFarmDirectMailCostSortOptions {
  field: keyof PpcFarmDirectMailCostData;
  direction: "asc" | "desc";
}

// NEW: Report History Ledger specific sort options
export interface PpcFarmReportHistorySortOptions {
  field: keyof PpcFarmReportHistoryLedgerData;
  direction: "asc" | "desc";
}

// Pagination options
export interface PpcFarmPaginationOptions {
  page: number;
  limit: number;
  total: number;
}

// App-specific user settings
export interface PpcFarmUserSettings {
  userId: string;
  notifications: boolean;
  defaultView: "grid" | "list";
  preferences: Record<string, any>;
  updatedAt: Timestamp;
}

// Activity log for audit trail
export interface PpcFarmActivityLog {
  id: string;
  entityType: string;
  entityId: string;
  action: "created" | "updated" | "deleted" | "synced";
  userId: string;
  userEmail: string;
  changes?: Record<string, { from: any; to: any }>;
  timestamp: Timestamp;
}

// Keepa Seller API Types for Phase 3: Seller Detail Scraper
export interface KeepaSellerCompetitor {
  percent: number;
  sellerId: string;
}

export interface KeepaSellerBrandStatistic {
  avg30SalesRank: number;
  brand: string;
  productCount: number;
  productCountWithAmazonOffer: number;
}

export interface KeepaSellerCategoryStatistic {
  avg30SalesRank: number;
  catId: number;
  productCount: number;
  productCountWithAmazonOffer: number;
}

export interface KeepaSellerFeedback {
  date: number;
  feedback: string;
  isStriked: boolean;
  rating: number;
}

export interface KeepaSellerResponse {
  address?: string[];
  avgBuyBoxCompetitors?: number;
  businessName?: string;
  buyBoxNewOwnershipRate?: number;
  buyBoxUsedOwnershipRate?: number;
  competitors?: KeepaSellerCompetitor[];
  csv?: number[][];
  currentRating?: number;
  currentRatingCount?: number;
  domainId?: number;
  hasFBA?: boolean;
  isScammer?: boolean;
  lastRatingUpdate?: number;
  lastUpdate?: number;
  negativeRating?: number[];
  neutralRating?: number[];
  phoneNumber?: string;
  positiveRating?: number[];
  ratingCount?: number[];
  ratingsLast30Days?: number;
  recentFeedback?: KeepaSellerFeedback[];
  sellerBrandStatistics?: KeepaSellerBrandStatistic[];
  sellerCategoryStatistics?: KeepaSellerCategoryStatistic[];
  sellerId: string;
  sellerName?: string;
  shipsFromChina?: boolean;
  totalStorefrontAsins?: number[]; // [lastUpdateTimestamp, actualCount] - per Keepa API docs
  trackedSince?: number;
}

// Firestore document structure for seller details (Admin SDK version)
export interface PpcFarmSellerDetailsDocument {
  sellerId: string;
  sellerData: KeepaSellerResponse;
  processedAt: AdminTimestamp;
  source: string;
  lastUpdated: AdminTimestamp;
}

// Client SDK version for frontend use
export interface PpcFarmSellerDetailsDocumentClient {
  sellerId: string;
  sellerData: KeepaSellerResponse;
  processedAt: Timestamp;
  source: string;
  lastUpdated: Timestamp;
}

// Keepa API response wrapper
export interface KeepaSellerApiResponse {
  sellers?: { [sellerId: string]: KeepaSellerResponse };
  tokensLeft?: number;
  refillRate?: number;
  refillIn?: number;
  timestamp?: number;
}

// Legacy interfaces for backward compatibility
export interface PpcFarmLedgerData extends PpcFarmTargetLedgerData {}
export interface TargetLedgerEntry extends PpcFarmTargetLedgerData {}

// ===== KEEPA PIPELINE MONITORING TYPES =====
// Schema for the 'keepaPipelineStatus' document
export interface KeepaPipelineStatus {
  ingestion: {
    lastRunTimestamp: Timestamp; // When the job last ran
    lastRunOutcome: 'success' | 'failure';
    durationSeconds: number; // How long it took
    fetchedFromKeepa: number; // e.g., 100,000
    newSellersAdded: number; // The key output metric
    keepaTokensLeft: number;
    errorMessage: string | null;
  };
  enrichment: {
    lastProcessedTimestamp: Timestamp; // Last time a batch was processed
    processedLastRun: number; // How many were processed in the last minute (should be ~15)
    newCompetitorsAdded?: number; // Optional: How many new competitors were added in the last run
    // We will calculate other enrichment stats on the frontend from the main collections.
  };
  // This field allows for manual override or notes from an admin.
  overrideStatus: {
    isOverridden: boolean;
    message: string;
  };
}

// Partial types for updates
export interface IngestionMetadata {
  lastRunTimestamp: Timestamp;
  lastRunOutcome: 'success' | 'failure';
  durationSeconds: number;
  fetchedFromKeepa: number;
  newSellersAdded: number;
  keepaTokensLeft: number;
  errorMessage: string | null;
}

export interface EnrichmentMetadata {
  lastProcessedTimestamp: Timestamp;
  processedLastRun: number;
  newCompetitorsAdded?: number; // Optional field for competitor extraction tracking
}

export interface OverrideStatus {
  isOverridden: boolean;
  message: string;
}

// ===== NEW MASTER SELLERS COLLECTION TYPES =====
// Flattened schema for optimized querying and cohort creation

// Address structure for queryable fields
export interface PpcFarmSellerAddress {
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
}

// Source types for seller data
export type PpcFarmSellerSource = 'keepa_api' | 'raw_scrape_g_sheet' | 'competitor_extraction';
export type PpcFarmSellerIdSource = 'keepa_topseller_daily' | 'raw_scrape_g_sheet' | 'competitor_extraction' | 'target_ledger_migration' | 'keepa_keyword_search';

// Master sellers document structure (Admin SDK version)
export interface PpcFarmSellersMasterDocument {
  sellerId: string;
  source: PpcFarmSellerSource;
  processedAt: AdminTimestamp;
  
  // Original rich data from source (Keepa API response or lean scrape data)
  sellerData: KeepaSellerResponse | Record<string, any>;
  
  // Denormalized queryable fields for high-performance filtering
  queryable_sellerName: string; // lowercase for case-insensitive search
  queryable_rating: number;
  queryable_ratingCount: number;
  queryable_brandCount: number;
  queryable_productCount: number; // Product count from totalStorefrontAsins[1]
  queryable_hasFBA: boolean | null;
  queryable_shipsFromChina: boolean | null;
  queryable_address: PpcFarmSellerAddress;
  queryable_isInTargetLedger: boolean; // Whether seller exists in Target Ledger
  queryable_isBrandExcluded: boolean; // NEW: Whether seller is excluded by brand rules
  
  // NEW: Address validation field for JIT validation cache
  addressValidation: PpcFarmAddressValidation;
}

// Client SDK version for frontend use
export interface PpcFarmSellersMasterDocumentClient {
  sellerId: string;
  source: PpcFarmSellerSource;
  processedAt: Timestamp;
  
  // Original rich data from source
  sellerData: KeepaSellerResponse | Record<string, any>;
  
  // Denormalized queryable fields
  queryable_sellerName: string;
  queryable_rating: number;
  queryable_ratingCount: number;
  queryable_brandCount: number;
  queryable_productCount: number; // Product count from totalStorefrontAsins[1]
  queryable_hasFBA: boolean | null;
  queryable_shipsFromChina: boolean | null;
  queryable_address: PpcFarmSellerAddress;
  queryable_isInTargetLedger: boolean; // Whether seller exists in Target Ledger
  queryable_isBrandExcluded: boolean; // NEW: Whether seller is excluded by brand rules
  
  // NEW: Address validation field for JIT validation cache
  addressValidation: PpcFarmAddressValidationClient;
}

// Seller ID work queue document structure
export interface PpcFarmSellerIdDocument {
  sellerId: string;
  source: PpcFarmSellerIdSource;
  status: 'unprocessed' | 'processed';
  addedAt: AdminTimestamp;
}

// Client SDK version for frontend use
export interface PpcFarmSellerIdDocumentClient {
  sellerId: string;
  source: PpcFarmSellerIdSource;
  status: 'unprocessed' | 'processed';
  addedAt: Timestamp;
}

// Collection names for pipeline monitoring and new ledger collections
export const PPC_FARM_COLLECTIONS = {
  // Existing collections
  METADATA: 'ppcFarm_metadata',
  SELLER_IDS: 'ppcFarm_sellerIds',
  SELLER_DETAILS: 'ppcFarm_sellerDetails',
  SELLERS_MASTER: 'ppcFarm_sellers_master',
  
  // Existing ledger collections
  TARGET_LEDGER: 'ppcFarm_targetLedger',
  COHORT_LEDGER: 'ppcFarm_cohortLedger',
  MAILING_LEDGER: 'ppcFarm_mailingLedger',
  
  // NEW: Additional ledger collections
  DIRECT_MAIL_COST: 'ppcFarm_directMailCost',
  REPORT_HISTORY_LEDGER: 'ppcFarm_reportHistoryLedger',
  
  // Sync status collection
  SYNC_STATUS: 'ppcFarmSync'
} as const;

// Document IDs for pipeline monitoring
export const PPC_FARM_DOCUMENTS = {
  KEEPA_PIPELINE_STATUS: 'keepaPipelineStatus',
  PIPELINE_ANALYTICS: 'pipelineAnalytics'
} as const;

// NEW: Spreadsheet configuration constants
export const PPC_FARM_SPREADSHEETS = {
  // Main PPC Farm spreadsheet (contains Mailing Ledger + Mailing Cost Dashboard)
  MAIN_SPREADSHEET_ID: '1eOjC0VYpZgle2gjEOP0tz9ulq39_GCqZpQ_HZjZwVzE',
  
  // Direct Mail Cost spreadsheet
  DIRECT_MAIL_COST_SPREADSHEET_ID: '1KgZLutTz_aK1olmJxj-Lo5PBzuvUAasE6JoP6QUbSJ0',
  
  // Report History spreadsheet
  REPORT_HISTORY_SPREADSHEET_ID: '1jYab0QgSANBLvXVyVsbG-E1HfwQfvk9a5byL6dMGoKo'
} as const;

// NEW: Sheet names for new data sources
export const PPC_FARM_SHEET_NAMES = {
  // Existing sheets
  TARGET_LEDGER: 'Target Ledger',
  COHORT_LEDGER: 'Cohort Ledger',
  MAILING_LEDGER: 'Mailing Ledger',
  
  // NEW: Additional sheets
  MAILING_COST_DASHBOARD: 'Mailing Cost Dashboard',
  DIRECT_MAIL_COST: 'Direct Mail Cost',
  REPORT_HISTORY_LEDGER: 'Report History Ledger'
} as const;

// ===== ANALYTICS SCOREBOARD SYSTEM TYPES =====
// Daily statistics structure for rolling 30-day window
export interface DailyStats {
  date: string; // YYYY-MM-DD format
  newSellersFound: number;
  sellersProcessed: number;
  competitorsFound: number;
}

// Source-specific totals for comprehensive tracking
export interface SourceTotals {
  keepa_topseller_daily: number;
  raw_scrape_g_sheet: number;
  competitor_extraction: number;
  target_ledger_migration: number;
}

// Pipeline analytics document structure (Admin SDK version)
export interface PipelineAnalyticsDocument {
  // Real-time totals (updated via atomic increments)
  totals: {
    newSellersFound: number;
    sellersProcessed: number;
    competitorsFound: number;
    totalSellersInMaster: number; // Total sellers in master collection
    sellersWithNoData: number; // All-time count of sellers with no_data_available status
    sourceCounts: {
      keepa_topseller_daily: number;
      raw_scrape_g_sheet: number;
      competitor_extraction: number;
      target_ledger_migration: number;
      keepa_keyword_search: number;
    };
    noDataReasonBreakdown: {
      keepa_api_no_data_returned: number;
    };
  };
  
  // Source breakdown totals (legacy - kept for compatibility)
  sourceBreakdown: {
    newSellersFound: SourceTotals;
    sellersProcessed: SourceTotals;
    competitorsFound: SourceTotals;
  };
  
  // Rolling 30-day daily statistics
  dailyStats: DailyStats[];
  
  // Pipeline status fields (migrated from keepaPipelineStatus)
  pipelineStatus: {
    ingestion: {
      lastRunTimestamp: AdminTimestamp;
      lastRunOutcome: 'success' | 'failure';
      durationSeconds: number;
      fetchedFromKeepa: number;
      newSellersAdded: number; // Used for Keepa Discovery 24h
      keepaTokensLeft: number;
      errorMessage: string | null;
    };
    enrichment: {
      lastProcessedTimestamp: AdminTimestamp;
      processedLastRun: number; // Used for status calculation
      newCompetitorsAdded?: number;
    };
    overrideStatus: {
      isOverridden: boolean;
      message: string;
    };
  };
  
  // Metadata
  lastUpdated: AdminTimestamp;
  version: number; // For schema evolution
}

// Client SDK version for frontend use
export interface PipelineAnalyticsDocumentClient {
  totals: {
    newSellersFound: number;
    sellersProcessed: number;
    competitorsFound: number;
    totalSellersInMaster: number; // Total sellers in master collection
    sellersWithNoData: number; // All-time count of sellers with no_data_available status
    sourceCounts: {
      keepa_topseller_daily: number;
      raw_scrape_g_sheet: number;
      competitor_extraction: number;
      target_ledger_migration: number;
      keepa_keyword_search: number;
    };
    noDataReasonBreakdown: {
      keepa_api_no_data_returned: number;
    };
  };
  
  sourceBreakdown: {
    newSellersFound: SourceTotals;
    sellersProcessed: SourceTotals;
    competitorsFound: SourceTotals;
  };
  
  dailyStats: DailyStats[];
  
  // Pipeline status fields (migrated from keepaPipelineStatus)
  pipelineStatus: {
    ingestion: {
      lastRunTimestamp: Timestamp;
      lastRunOutcome: 'success' | 'failure';
      durationSeconds: number;
      fetchedFromKeepa: number;
      newSellersAdded: number; // Used for Keepa Discovery 24h
      keepaTokensLeft: number;
      errorMessage: string | null;
    };
    enrichment: {
      lastProcessedTimestamp: Timestamp;
      processedLastRun: number; // Used for status calculation
      newCompetitorsAdded?: number;
    };
    overrideStatus: {
      isOverridden: boolean;
      message: string;
    };
  };
  
  lastUpdated: Timestamp;
  version: number;
}

// Enhanced dashboard metrics combining existing and new analytics
export interface DashboardMetricsEnhanced {
  // Existing metrics (from current system)
  totalSellerIds: number;
  unprocessedSellerIds: number;
  processedSellerIds: number;
  totalSellerDetails: number;
  
  // New analytics metrics
  analytics: {
    // Historical trends (24h, 7d, 30d)
    trends: {
      newSellersFound: {
        last24h: number;
        last7d: number;
        last30d: number;
      };
      sellersProcessed: {
        last24h: number;
        last7d: number;
        last30d: number;
      };
      competitorsFound: {
        last24h: number;
        last7d: number;
        last30d: number;
      };
    };
    
    // Source totals
    sourceTotals: {
      totalSellersFromKeepa: number;
      rawScrapeTotalSellers: number;
      competitorsTotalSellers: number;
    };
    
    // Processing velocity (items per day)
    velocity: {
      avgSellersProcessedPerDay: number;
      avgCompetitorsFoundPerDay: number;
    };
  };
  
  // Pipeline status (existing)
  pipelineStatus?: KeepaPipelineStatus;
  lastUpdated: Timestamp;
}

// Analytics increment operations for atomic updates
export interface AnalyticsIncrementOperation {
  field: 'newSellersFound' | 'sellersProcessed' | 'competitorsFound';
  source: PpcFarmSellerIdSource;
  amount: number;
  date?: string; // YYYY-MM-DD, defaults to today
}

// Trend calculation result
export interface TrendCalculation {
  last24h: number;
  last7d: number;
  last30d: number;
}

// Analytics service response types
export interface AnalyticsServiceResponse {
  success: boolean;
  error?: string;
  data?: any;
}

// Analytics initialization data
export interface AnalyticsInitializationData {
  totalSellersFromKeepa: number;
  rawScrapeTotalSellers: number;
  competitorsTotalSellers: number;
}

// Enhanced dashboard metrics for new analytics UI
export interface EnhancedDashboardMetrics {
  // Live Pipeline Status
  overallStatus: 'healthy' | 'warning' | 'error';
  sellersInQueue: number;
  keepaDiscovery24h: number;
  competitorDiscovery24h: number;
  
  // Seller Database Overview
  totalEnrichedSellers: number;
  fromKeepaTopSellers: {
    count: number;
    percentage: number;
  };
  fromCompetitors: {
    count: number;
    percentage: number;
  };
  fromRawScrape: {
    count: number;
    percentage: number;
  };
  fromTargetLedger: {
    count: number;
    percentage: number;
  };
  
  // Performance Trends (matching service method return structure)
  newSellersFound: {
    last24h: number;
    last7d: number;
    last30d: number;
  };
  sellersProcessed: {
    last24h: number;
    last7d: number;
    last30d: number;
  };
  
  // Additional metadata
  estimatedTimeToClear: string;
  lastUpdated: Timestamp;
}

// ===== PPC FARM SYNC STATUS TYPES =====
// Individual ledger sync metrics interface (matches existing Firebase function structure)
export interface PpcFarmLedgerSyncMetrics {
  ledgerName: 'targetLedger' | 'cohortLedger' | 'mailingLedger' | 'directMailCost' | 'reportHistoryLedger';
  status: "success" | "error" | "skipped";
  totalRows: number;
  validRows: number;
  processedRows: number;
  skippedRows: number;
  dataQualityIssues: number;
  filteredOutRows: number;
  batches: number;
  newDocuments?: number;
  rowsChecked?: number;
  joinedRecords?: number; // NEW: For mailing ledger cost dashboard joins
  campaignCosts?: number; // NEW: For direct mail cost campaign vs operational breakdown
  operationalCosts?: number; // NEW: For direct mail cost campaign vs operational breakdown
  error?: string;
}

// Overall sync status interface (matches existing ppcFarmSync collection structure)
export interface PpcFarmSyncStatus {
  // Top-level job status fields
  inProgress: boolean;
  lastAttempt: Timestamp | AdminTimestamp;
  lastSync: Timestamp | AdminTimestamp;
  lastSyncSuccess: boolean;
  startTime: Timestamp | AdminTimestamp;
  endTime: Timestamp | AdminTimestamp;
  error: string | null;

  // Aggregate metrics across all ledgers
  ledgersProcessed: number;
  totalRowsProcessed: number;
  totalValidRows: number;
  totalSkippedRows: number;
  totalDataQualityIssues: number;
  totalBatches: number;
  totalChanges: number;
  
  // Additional aggregate fields from existing structure
  newDocuments: number;
  processedRows: number;
  rowsChecked: number;
  skippedRows: number;
  validRows: number;
  totalRows: number;

  // Array of individual ledger metrics
  ledgerMetrics: PpcFarmLedgerSyncMetrics[];
}

// Client SDK version for frontend use
export interface PpcFarmSyncStatusClient {
  // Top-level job status fields
  inProgress: boolean;
  lastAttempt: Timestamp;
  lastSync: Timestamp;
  lastSyncSuccess: boolean;
  startTime: Timestamp;
  endTime: Timestamp;
  error: string | null;

  // Aggregate metrics across all ledgers
  ledgersProcessed: number;
  totalRowsProcessed: number;
  totalValidRows: number;
  totalSkippedRows: number;
  totalDataQualityIssues: number;
  totalBatches: number;
  totalChanges: number;
  
  // Additional aggregate fields from existing structure
  newDocuments: number;
  processedRows: number;
  rowsChecked: number;
  skippedRows: number;
  validRows: number;
  totalRows: number;

  // Array of individual ledger metrics
  ledgerMetrics: PpcFarmLedgerSyncMetrics[];
}

// Sync operation result for individual ledgers
export interface PpcFarmSyncResult {
  success: boolean;
  ledgerName: string;
  metrics: PpcFarmLedgerSyncMetrics;
  error?: string;
  duration?: number; // milliseconds
}

// Bulk sync operation result
export interface PpcFarmBulkSyncResult {
  success: boolean;
  totalLedgers: number;
  successfulLedgers: number;
  failedLedgers: number;
  results: PpcFarmSyncResult[];
  overallMetrics: Omit<PpcFarmSyncStatus, 'ledgerMetrics'>;
  duration: number; // milliseconds
  error?: string;
}

// ===== ADDRESS VALIDATION TYPES =====
// Address validation system for PPC Farm Mail Tracker Cohort Builder

// NEW: Address validation field structure for sellers master collection
export interface PpcFarmAddressValidation {
  status: 'PENDING' | 'VALIDATED' | 'FAILED' | 'SKIPPED';
  validatedAt?: AdminTimestamp;
  validationProvider?: 'google_address_validation_api';
  validationCost?: number;
  validationResult?: {
    isValid: boolean;
    standardizedAddress?: {
      line1: string;
      line2?: string;
      city: string;
      state: string;
      zipCode5: string;
      zipCode4?: string;
      country: string;
    };
    validationDetails?: {
      addressComplete: boolean;
      uspsDeliverable: boolean;
      dpvConfirmation?: string;
    };
  };
  errorMessage?: string;
  lastAttemptAt?: AdminTimestamp;
  retryCount?: number;
}

// Client SDK version for address validation
export interface PpcFarmAddressValidationClient {
  status: 'PENDING' | 'VALIDATED' | 'FAILED' | 'SKIPPED';
  validatedAt?: Timestamp;
  validationProvider?: 'google_address_validation_api';
  validationCost?: number;
  validationResult?: {
    isValid: boolean;
    standardizedAddress?: {
      line1: string;
      line2?: string;
      city: string;
      state: string;
      zipCode5: string;
      zipCode4?: string;
      country: string;
    };
    validationDetails?: {
      addressComplete: boolean;
      uspsDeliverable: boolean;
      dpvConfirmation?: string;
    };
  };
  errorMessage?: string;
  lastAttemptAt?: Timestamp;
  retryCount?: number;
}

// Input structure for address validation
export interface AddressValidationInput {
  sellerId: string;
  sellerName: string;
  originalAddress: {
    line1?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
}

// Google Address Validation API request structure
export interface GoogleAddressValidationRequest {
  address: {
    addressLines?: string[];
    locality?: string;
    administrativeArea?: string;
    postalCode?: string;
    regionCode?: string;
  };
  enableUspsCass: boolean; // CRITICAL: Enable USPS CASS certification
}

// Address validation result structure
export interface AddressValidationResult {
  sellerId: string;
  sellerName: string;
  businessName?: string; // NEW: Business name from seller data
  status: 'VALIDATED' | 'NEEDS_CONFIRMATION' | 'NEEDS_FIXING' | 'MISSING_UNIT' | 'UNVALIDATED' | 'ERROR';
  statusReason?: string;
  originalAddress: {
    line1?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
  standardizedAddress?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    zipCode5: string;
    zipCode4?: string;
    country: string;
    fullCityStateZip?: string;
  };
  validationDetails?: {
    addressComplete: boolean;
    hasUnconfirmedComponents: boolean;
    hasInferredComponents: boolean;
    hasReplacedComponents: boolean;
    hasSpellCorrectedComponents: boolean;
    dpvConfirmation?: string;
    uspsDeliverable: boolean;
    validationGranularity?: string;
    possibleNextAction?: string;
    missingComponentTypes?: string[];
    unresolvedTokens?: string[];
    suspiciousComponents?: string[];
  };
  errorMessage?: string;
  processedAt: Timestamp;
}

// Validation session for tracking progress
export interface ValidationSession {
  sessionId: string;
  userId: string;
  userEmail: string;
  status: 'processing' | 'completed' | 'error' | 'timeout';
  totalAddresses: number;
  processedAddresses: number;
  validatedAddresses: number;
  unvalidatedAddresses: number;
  results: AddressValidationResult[];
  error?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  completedAt?: Timestamp;
  estimatedDurationMs?: number;
}

// Progress tracking for real-time updates
export interface ValidationProgress {
  sessionId: string;
  status: 'PROCESSING' | 'COMPLETED' | 'ERROR' | 'TIMEOUT';
  progress: {
    total: number;
    processed: number;
    validated: number;
    unvalidated: number;
    errors: number;
    percentage: number;
  };
  results: AddressValidationResult[];
  error?: string;
  isComplete: boolean;
  estimatedTimeRemaining?: number;
}

// Service response types
export interface AddressValidationServiceResponse {
  success: boolean;
  sessionId?: string;
  totalAddresses?: number;
  estimatedDurationMs?: number;
  message?: string;
  error?: string;
}

export interface ValidationProgressResponse {
  success: boolean;
  session?: ValidationProgress;
  error?: string;
}

// Modal state management
export interface AddressValidationModalState {
  isOpen: boolean;
  stage: 'loading' | 'processing' | 'results' | 'error';
  sessionId?: string;
  sellerIds: string[];
  progress?: ValidationProgress;
  error?: string;
}

// Filter options for validation results
export interface ValidationResultsFilter {
  showAll: boolean;
  showUnvalidatedOnly: boolean;
}

// Statistics for validation results summary
export interface ValidationResultsStats {
  total: number;
  validated: number;
  needsConfirmation: number;
  needsFixing: number;
  missingUnit: number;
  unvalidated: number;
  errors: number;
  validatedPercentage: number;
  needsConfirmationPercentage: number;
  needsFixingPercentage: number;
  missingUnitPercentage: number;
  unvalidatedPercentage: number;
  errorPercentage: number;
}

// Collection names for address validation
export const ADDRESS_VALIDATION_COLLECTIONS = {
  SESSIONS: 'ppcFarm_addressValidationSessions',
  RESULTS: 'ppcFarm_addressValidationResults'
} as const;

// Configuration constants
export const ADDRESS_VALIDATION_CONFIG = {
  MAX_SELLERS_PER_VALIDATION: 2000,
  BATCH_SIZE: 12,
  MAX_CONCURRENT_BATCHES: 3,
  SESSION_TIMEOUT_MS: 30 * 60 * 1000, // 30 minutes
  POLLING_INTERVAL_MS: 2000, // 2 seconds
  MAX_POLLING_ATTEMPTS: 900, // 30 minutes worth of polling
  PROGRESS_UPDATE_INTERVAL_MS: 1000 // 1 second
} as const;

// ===== KEYWORD DISCOVERY PIPELINE TYPES =====
// Types for the getSellersFromKeywords function and keyword-based seller discovery

// Keyword document structure for the discovery pipeline
export interface PpcFarmKeywordDocument {
  term: string; // The search keyword (e.g., "organic coffee beans")
  status: 'pending' | 'in_progress' | 'completed' | 'error'; // Processing status
  last_processed: AdminTimestamp | null; // When it was last processed
  created_at: AdminTimestamp; // When the keyword was added
  created_by: string; // User email who added the keyword
  priority: number; // Order priority (based on entry order)
  error_message?: string; // Error details if status is 'error'
  retry_count?: number; // Number of retry attempts
  stats?: {
    sellers_found: number; // Number of sellers discovered from this keyword
    asins_discovered: number; // Number of ASINs found during search
    processed_at: AdminTimestamp | null; // When processing completed
  };
  metadata?: {
    source: 'manual_input' | 'bulk_import'; // How the keyword was added
    notes?: string; // Optional user notes
  };
}

// Client SDK version for frontend use
export interface PpcFarmKeywordDocumentClient {
  term: string;
  status: 'pending' | 'in_progress' | 'completed' | 'error';
  last_processed: Timestamp | null;
  created_at: Timestamp;
  created_by: string;
  priority: number;
  error_message?: string;
  retry_count?: number;
  stats?: {
    sellers_found: number;
    asins_discovered: number;
    processed_at: Timestamp | null;
  };
  metadata?: {
    source: 'manual_input' | 'bulk_import';
    notes?: string;
  };
}

// Keyword statistics for dashboard display
export interface PpcFarmKeywordStats {
  total_keywords: number;
  pending_keywords: number;
  in_progress_keywords: number;
  completed_keywords: number;
  error_keywords: number;
  total_sellers_found: number;
  total_asins_discovered: number;
}

// Keyword validation result
export interface KeywordValidationResult {
  keyword: string;
  is_valid: boolean;
  is_duplicate: boolean;
  error_message?: string;
}

// Bulk keyword input data
export interface BulkKeywordInput {
  keywords: string[];
  user_email: string;
  source: 'manual_input' | 'bulk_import';
  notes?: string;
}

// Keyword filters for the management interface
export interface PpcFarmKeywordFilters {
  search: string;
  status: 'all' | 'pending' | 'in_progress' | 'completed' | 'error';
  created_by: string;
  date_range: {
    start: string | null;
    end: string | null;
  };
}

// Keyword sort options
export interface PpcFarmKeywordSortOptions {
  field: 'term' | 'status' | 'created_at' | 'priority' | 'sellers_found';
  direction: 'asc' | 'desc';
}

// Seller-keyword mapping document structure
export interface PpcFarmSellerKeywordMapDocument {
  sellerId: string; // The Amazon seller ID
  keyword: string; // The keyword that discovered this seller
  discoveredAt: AdminTimestamp; // When the discovery happened
  asin?: string; // Optional: The ASIN that led to this seller discovery
}

// Client SDK version for frontend use
export interface PpcFarmSellerKeywordMapDocumentClient {
  sellerId: string;
  keyword: string;
  discoveredAt: Timestamp;
  asin?: string;
}

// Keepa Search API response structure
export interface KeepaSearchApiResponse {
  asinList?: string[]; // List of ASINs returned by search
  tokensLeft?: number; // Remaining API tokens
  refillRate?: number; // Token refill rate
  refillIn?: number; // Time until next refill
  timestamp?: number; // Response timestamp
}

// Keepa Product API response structure (for buybox data)
export interface KeepaProductApiResponse {
  products?: KeepaProductData[]; // Array of product objects
  tokensLeft?: number; // Remaining API tokens
  refillRate?: number; // Token refill rate
  refillIn?: number; // Time until next refill
  timestamp?: number; // Response timestamp
}

// Individual product data from Keepa Product API
export interface KeepaProductData {
  asin: string; // Amazon Standard Identification Number
  title?: string; // Product title
  buyBoxSellerIdHistory?: (number | string)[]; // Alternating timestamps and seller IDs
  [key: string]: any; // Allow other Keepa product fields
}

// Collection names for keyword discovery pipeline
export const PPC_FARM_KEYWORD_COLLECTIONS = {
  KEYWORDS: 'ppcFarm_keywords',
  SELLER_KEYWORD_MAP: 'ppcFarm_sellerKeywordMap'
} as const;

// Validation status type for address validation
export type ValidationStatus = 'VALIDATED' | 'NEEDS_CONFIRMATION' | 'NEEDS_FIXING' | 'MISSING_UNIT' | 'UNVALIDATED' | 'ERROR';

// ===== PHASE 1: COHORT REVIEW SYSTEM TYPES =====
// Types for the PPC Farm Mail Tracker Cohort Builder Enhancement Phase 1

// Collection names for cohort review system
export const COHORT_REVIEW_COLLECTIONS = {
  COHORTS: 'ppcFarm_cohorts',
  COHORT_MEMBERS: 'ppcFarm_cohort_members'
} as const;

/**
 * Document structure for ppcFarm_cohorts collection
 * This holds one document per cohort
 */
export interface PpcFarmCohort {
  cohortNumber: number;          // Auto-incrementing (e.g., 35, 36...)
  cohortNotes: string;           // From the new UI input
  createdByName: string;         // From the new UI input
  createdAt: Timestamp;          // Server timestamp
  status: 'IN_REVIEW';           // This is the initial status
  originalFilters: object;       // The filter criteria from the Cohort Builder
  stats: {
    totalSellers: number;        // e.g., 4000
    problemsFound: number;       // Default to 0
    problemsSolved: number;      // Default to 0
  };
}

/**
 * Admin SDK version for backend use
 */
export interface PpcFarmCohortAdmin {
  cohortNumber: number;
  cohortNotes: string;
  createdByName: string;
  createdAt: AdminTimestamp;
  status: 'IN_REVIEW';
  originalFilters: object;
  stats: {
    totalSellers: number;
    problemsFound: number;
    problemsSolved: number;
  };
}

/**
 * Document structure for ppcFarm_cohort_members collection
 * This holds one document per seller in the cohort
 */
export interface PpcFarmCohortMember {
  cohortId: string;              // The Firestore ID of the parent doc in ppcFarm_cohorts
  sellerId: string;              // The ID from ppcFarm_sellers_master
  originalName: string;          // A snapshot, copied from ppcFarm_sellers_master
  originalAddress: string;       // A snapshot, copied from ppcFarm_sellers_master
  addressValidationResult: object; // Store the FULL JSON response object from the Google API here
  workingName: string;           // Initialize as a copy of originalName
  workingAddress: string;        // Initialize as a copy of originalAddress
  problemFlags: {                // Initialize all to false. Phase 2 will fill this.
    isDuplicate: boolean;
    isLongName: boolean;
    isAllCaps: boolean;
    isAllSmall: boolean;
  };
  problemStatus: 'NEEDS_REVIEW'; // Initial status for every member
  cleanedByName: string | null;  // Will be filled in Phase 3
}

/**
 * Admin SDK version for backend use
 */
export interface PpcFarmCohortMemberAdmin {
  cohortId: string;
  sellerId: string;
  originalName: string;
  originalAddress: string;
  addressValidationResult: object;
  workingName: string;
  workingAddress: string;
  problemFlags: {
    isDuplicate: boolean;
    isLongName: boolean;
    isAllCaps: boolean;
    isAllSmall: boolean;
  };
  problemStatus: 'NEEDS_REVIEW';
  cleanedByName: string | null;
}

/**
 * Input interface for the startCohortReview Cloud Function
 */
export interface StartCohortReviewInput {
  creatorName: string;
  cohortNotes: string;
  cohortNumber: number;
  sellerIds: string[];
  validationResults: object[];   // Raw JSON from Google API
  filters: object;               // Original filter criteria
}

/**
 * Response interface for the startCohortReview Cloud Function
 */
export interface StartCohortReviewResponse {
  success: boolean;
  cohortId?: string;
  message?: string;
  error?: string;
}

/**
 * Form data for the new UI inputs in CohortReviewPage
 */
export interface CohortReviewFormData {
  creatorName: string;
  cohortNotes: string;
  cohortNumber: number;
}

/**
 * Service interface for cohort review operations
 */
export interface CohortReviewService {
  getNextCohortNumber(): Promise<number>;
  startCohortReview(input: StartCohortReviewInput): Promise<StartCohortReviewResponse>;
}
